import { createApi, retry } from '@reduxjs/toolkit/query/react';
import { CommunityId } from 'domain/CommunityId';
import { axiosBaseQuery } from 'modules/api/utils';
import { UrlNames } from 'modules/app/constants';
import { configService } from 'modules/app/services';

import {
  type TagServiceCategoriesResponse,
  type TagServiceUserTagsResponse,
  mapCategoriesFromTagService,
  mapUserTagsFromTagService,
  mapUserTagsToTagService,
} from './tagServiceMappers';

import type { TagCategory, TagSelections } from './types';

const getBaseURL = () => {
  const url = configService.getUrl(UrlNames.TagApi);
  if (!url) throw new Error('VITE_APP_TAG_API_URL is not configured');
  return url;
};

export const tagServiceApi = createApi({
  reducerPath: 'tagServiceApi',
  baseQuery: retry(axiosBaseQuery({ baseURL: getBaseURL }), { maxRetries: 0 }),
  tagTypes: ['EVENT_TAG_CATEGORIES', 'MY_EVENT_TAGS'],
  // Participants only. Categories are WRITTEN through the backend's event save: the tag service asks
  // permissions-service for natter:manage with no event context, so it can only ever authorise a community
  // admin and would 403 an event host. Reads, and a participant's own tag selections, stay here -- the tag
  // service authorises those on identity alone.
  endpoints: (builder) => ({
    getEventTagCategories: builder.query<TagCategory[], { communityId: CommunityId; eventId: number }>({
      query: ({ communityId, eventId }) => ({
        url: `/v1/communities/${communityId}/events/${eventId}/tag-categories`,
      }),
      transformResponse: mapCategoriesFromTagService,
      providesTags: (_result, _error, { eventId }) => [{ type: 'EVENT_TAG_CATEGORIES', id: eventId }],
    }),

    getMyEventTags: builder.query<TagSelections, { communityId: CommunityId; eventId: number; accountId: number }>({
      query: ({ communityId, eventId, accountId }) => ({
        url: `/v1/communities/${communityId}/events/${eventId}/tags/user/${accountId}`,
      }),
      transformResponse: mapUserTagsFromTagService,
      providesTags: (_result, _error, { eventId }) => [{ type: 'MY_EVENT_TAGS', id: eventId }],
    }),

    setMyEventTags: builder.mutation<
      void,
      { communityId: CommunityId; eventId: number; accountId: number; selections: TagSelections }
    >({
      query: ({ communityId, eventId, accountId, selections }) => ({
        url: `/v1/communities/${communityId}/events/${eventId}/tags/user/${accountId}`,
        method: 'PUT',
        data: mapUserTagsToTagService(selections),
      }),
      invalidatesTags: (_result, _error, { eventId }) => [{ type: 'MY_EVENT_TAGS', id: eventId }],
    }),
  }),
});
